export function countContentByType(playlist, contentType) {
    if (!playlist.sections) return 0
    return playlist.sections.reduce((count, section) => {
        return count + section.content.filter(match('type', contentType)).length
    }, 0)
}

export function filterArray(arr, search) {
    if (!search || search.trim() === '') return arr;

    let lowSearch = search.toLowerCase().trim();

    // Use a Set to track unique identifiers (we'll use email or userId if available)
    const uniqueIds = new Set();

    return arr.filter((item) => {
        // Generate a unique identifier for this item
        const uniqueId = item.email || item.userId || JSON.stringify(item);

        // Check if any string value in the object contains the search term
        const shouldInclude = Object.entries(item).some(([key, val]) => {
            if (val === null || val === undefined) return false;

            // For string values, directly check for match
            if (typeof val === 'string') {
                return val.toLowerCase().includes(lowSearch);
            }

            // For numeric values, convert to string first
            if (typeof val === 'number') {
                return String(val).includes(lowSearch);
            }

            // For nested objects, search recursively
            if (typeof val === 'object' && val !== null) {
                // Handle arrays
                if (Array.isArray(val)) {
                    return val.some(arrayItem => {
                        if (typeof arrayItem === 'string') {
                            return arrayItem.toLowerCase().includes(lowSearch);
                        } else if (typeof arrayItem === 'number') {
                            return String(arrayItem).includes(lowSearch);
                        } else if (typeof arrayItem === 'object' && arrayItem !== null) {
                            return filterArray([arrayItem], search).length > 0;
                        }
                        return false;
                    });
                }

                // Handle nested objects - search their properties
                return Object.entries(val).some(([nestedKey, nestedVal]) => {
                    if (typeof nestedVal === 'string') {
                        return nestedVal.toLowerCase().includes(lowSearch);
                    } else if (typeof nestedVal === 'number') {
                        return String(nestedVal).includes(lowSearch);
                    } else if (typeof nestedVal === 'object' && nestedVal !== null) {
                        return filterArray([nestedVal], search).length > 0;
                    }
                    return false;
                });
            }

            // For other types, try string conversion if simple type
            if (typeof val !== 'object') {
                try {
                    return String(val).toLowerCase().includes(lowSearch);
                } catch (e) {
                    return false;
                }
            }

            return false;
        });

        // Only include this item if it matches the search AND we haven't seen it before
        if (shouldInclude && !uniqueIds.has(uniqueId)) {
            uniqueIds.add(uniqueId);
            return true;
        }

        return false;
    });
}

export function findContentIndexesByPk(playlist, pk) {
    let result = { contentIndex: null, sectionIndex: null }

    playlist.sections.forEach((section, sectionIndex) => {
        section.content.forEach((content, contentIndex) => {
            if (pk === content.pk) {
                result = {
                    contentIndex,
                    contentName: content.meta.title[0].text,
                    sectionIndex,
                    sectionName: section.title[0].text
                }
            }
        })
    })

    return result
}

export function findDuplicateContentByType(playlist, contentType) {
    const set = new Set([])
    let duplicates = {}

    playlist.sections.forEach((section, sectionIndex) => {
        section.content.forEach((content, contentIndex) => {
            if (content.type === contentType) {
                if (set.has(content.pk)) {
                    // Check if first or n-th duplicate
                    if (!duplicates[content.pk]) {
                        duplicates[content.pk] = {
                            occurrences: getAllOccurrencesById(playlist, content.pk)
                        }
                    }
                } else {
                    set.add(content.id)
                }
            }
        })
    })

    return duplicates
}

export function findValue(arr, string) {
    return arr.filter((x) => x.name.toLowerCase().includes(string.toLowerCase()))
}

function getAllOccurrencesById(playlist, contentPk) {
    let occurrences = []

    playlist.sections.forEach((section, sectionIndex) => {
        section.content.forEach((content, contentIndex) => {
            if (content.pk === contentPk) {
                occurrences.push({
                    content,
                    sectionIndex,
                    contentIndex
                })
            }
        })
    })

    return occurrences
}

export const groupBy = (items, key) =>
    items.reduce(
        (result, item) => ({
            ...result,
            [item[key]]: [...(result[item[key]] || []), item],
        }),
        {}
    )

export function groupByArray(xs, key) {
    return xs.reduce(function (rv, x) {
        let v = key instanceof Function ? key(x) : x[key]
        let el = rv.find((r) => r && r.key === v)
        if (el) {
            el.values.push(x)
        } else {
            rv.push({ key: v, values: [x] })
        }
        return rv
    }, [])
}

export function immutableReplace(arr, index, newItem) {
    return arr
        .slice(0, index)
        .concat(newItem)
        .concat(arr.slice(index + 1))
}

export function includesAny(arr, any) {
    return any.map((item) => arr.includes(item)).includes(true)
}

export function isContentComplete(content, playlist, trainingResult) {
    if (!trainingResult) return false
    if (content.type === 'VIDEO' && trainingResult.videosCompleted) {
        return trainingResult.videosCompleted.some(pk => pk === content.pk)
    } else if (content.type === 'QUIZ' && trainingResult.quizzesCompleted) {
        return trainingResult.quizzesCompleted.some(pk => pk === content.pk)
    } else if (content.type === 'CONNECTION' && !content.hidden) {
        const contentIndexes = findContentIndexesByPk(playlist, content.to)
        return isSectionComplete(playlist.sections[contentIndexes.sectionIndex], playlist, trainingResult)
    } else {
        return true
    }
}

export function isSectionComplete(section, playlist, trainingResult) {
    return section.content.every(content => isContentComplete(content, playlist, trainingResult))
}

export function match(key, val) {
    return (obj) => obj[key] === val
}

export const removeDuplicates = (data) =>
    data.reduce((a, b) => {
        if (a.indexOf(b) < 0) a.push(b)
        return a
    }, [])

export function deepFilter(arr, searchString) {
    if (!searchString || searchString.trim() === '') return arr;

    let _keyword = searchString.toLowerCase().trim();

    // Use a Set to track unique identifiers (we'll use email or userId if available)
    const uniqueIds = new Set();

    return arr.filter(item => {
        if (!item) return false;

        // Generate a unique identifier for this item
        // Using email is ideal since it's guaranteed to be unique for a user
        const uniqueId = item.email || item.userId || JSON.stringify(item);

        // Check if we should include this item based on search criteria
        let shouldInclude = false;

        if (typeof item === 'object') {
            // Search through all properties of the object
            shouldInclude = Object.entries(item).some(([key, value]) => {
                // Direct string match
                if (typeof value === 'string' && value.toLowerCase().includes(_keyword)) {
                    return true;
                }

                // Number conversion match
                if (typeof value === 'number' && String(value).includes(_keyword)) {
                    return true;
                }

                // Handle nested objects
                if (typeof value === 'object' && value !== null) {
                    return deepFilter([value], _keyword).length > 0;
                }

                // Try string conversion for other types
                if (value !== null && value !== undefined) {
                    try {
                        return String(value).toLowerCase().includes(_keyword);
                    } catch (e) {
                        return false;
                    }
                }

                return false;
            });
        } else if (typeof item === 'string') {
            shouldInclude = item.toLowerCase().includes(_keyword);
        } else if (typeof item === 'number') {
            shouldInclude = String(item).includes(_keyword);
        }

        // Only include this item if it matches the search AND we haven't seen it before
        if (shouldInclude && !uniqueIds.has(uniqueId)) {
            uniqueIds.add(uniqueId);
            return true;
        }

        return false;
    });
}